import { analyteForKey, evaluateDerived } from './assay-analytes.js';
import { owningTestsByAssay, storedNumber } from './order-results.js';
export const ASSAY_PANEL_VERSION = 1;
export const PANEL_OPS = ['>', '>=', '<', '<='];
const isPlainObject = (v) => typeof v === 'object' && v !== null && !Array.isArray(v);
const CONSTANT_NAME = /^[a-z][a-z0-9_]{0,63}$/;
const MAX_ASSAYS = 50;
const MAX_RULES = 50;
function validateRef(raw, at) {
    if (typeof raw === 'number') {
        return Number.isInteger(raw) && raw > 0 ? { ref: raw } : { error: `${at}: an assay id must be a positive integer` };
    }
    if (typeof raw === 'string' && raw.trim())
        return { ref: raw.trim() };
    return { error: `${at}: must be an assay id or title` };
}
const refKey = (ref) => (typeof ref === 'number' ? `#${ref}` : `"${ref}"`);
function validateRefList(raw, at) {
    if (!Array.isArray(raw) || raw.length === 0)
        return `${at}: must be a non-empty array of assay ids or titles`;
    if (raw.length > MAX_ASSAYS)
        return `${at}: at most ${MAX_ASSAYS} assays`;
    const out = [];
    const seen = new Set();
    for (let i = 0; i < raw.length; i++) {
        const checked = validateRef(raw[i], `${at}[${i}]`);
        if ('error' in checked)
            return checked.error;
        const { ref } = checked;
        if (seen.has(refKey(ref)))
            return `${at}[${i}]: duplicate assay ${refKey(ref)}`;
        seen.add(refKey(ref));
        out.push(ref);
    }
    return out;
}
function validateConstants(raw) {
    if (!isPlainObject(raw))
        return 'constants: must be an object of { value, unit?, description? } by name';
    const out = {};
    for (const [name, c] of Object.entries(raw)) {
        const at = `constants.${name}`;
        if (!CONSTANT_NAME.test(name))
            return `${at}: a name is lower case letters, digits and _, starting with a letter`;
        if (!isPlainObject(c))
            return `${at}: must be an object { value, unit?, description? }`;
        if (typeof c.value !== 'number' || !Number.isFinite(c.value))
            return `${at}.value: must be a finite number`;
        const constant = { value: c.value };
        if (c.unit !== undefined) {
            if (c.unit !== null && typeof c.unit !== 'string')
                return `${at}.unit: must be a string or null`;
            constant.unit = c.unit === null ? null : c.unit.trim();
        }
        if (c.description !== undefined) {
            if (typeof c.description !== 'string')
                return `${at}.description: must be a string`;
            constant.description = c.description;
        }
        out[name] = constant;
    }
    return out;
}
function validateRule(raw, at, constants) {
    if (!isPlainObject(raw))
        return `${at}: must be an object { when, add }`;
    const when = raw.when;
    if (!isPlainObject(when))
        return `${at}.when: must be an object { assay, key, op, value }`;
    const assay = validateRef(when.assay, `${at}.when.assay`);
    if ('error' in assay)
        return assay.error;
    if (typeof when.key !== 'string' || !when.key.trim())
        return `${at}.when.key: required non-empty string`;
    if (!PANEL_OPS.includes(when.op))
        return `${at}.when.op: must be one of ${PANEL_OPS.join(' ')}`;
    if (typeof when.value !== 'string' || !(when.value in constants)) {
        return `${at}.when.value: must name a panel constant (${Object.keys(constants).join(', ') || 'none declared'})`;
    }
    const add = validateRefList(raw.add, `${at}.add`);
    if (typeof add === 'string')
        return add;
    const rule = { when: { assay: assay.ref, key: when.key.trim(), op: when.op, value: when.value }, add };
    if (raw.description !== undefined) {
        if (typeof raw.description !== 'string')
            return `${at}.description: must be a string`;
        rule.description = raw.description;
    }
    return rule;
}
/**
 * Validate and normalise an `assay.panel` value. Assays may be named by id or
 * by title; a title is kept as written for the LIMS to resolve on write (see
 * resolvePanelAssays). Enforces: at least one start assay, no duplicate
 * within a list, every rule compares with a declared constant, a rule's
 * condition assay is one the panel requests, and no rule adds a start assay.
 */
export function validateAssayPanel(value) {
    if (!isPlainObject(value))
        return { error: 'panel: must be an object { version, start, constants?, reflex? }' };
    if (value.version !== ASSAY_PANEL_VERSION) {
        return { error: `panel.version: must be ${ASSAY_PANEL_VERSION} (got ${JSON.stringify(value.version)})` };
    }
    const start = validateRefList(value.start, 'start');
    if (typeof start === 'string')
        return { error: `panel.${start}` };
    const out = { version: ASSAY_PANEL_VERSION, start };
    let constants = {};
    if (value.constants !== undefined) {
        const checked = validateConstants(value.constants);
        if (typeof checked === 'string')
            return { error: `panel.${checked}` };
        constants = checked;
        out.constants = checked;
    }
    if (value.reflex !== undefined) {
        if (!Array.isArray(value.reflex))
            return { error: 'panel.reflex: must be an array of { when, add }' };
        if (value.reflex.length > MAX_RULES)
            return { error: `panel.reflex: at most ${MAX_RULES} rules` };
        const rules = [];
        for (let i = 0; i < value.reflex.length; i++) {
            const rule = validateRule(value.reflex[i], `reflex[${i}]`, constants);
            if (typeof rule === 'string')
                return { error: `panel.${rule}` };
            rules.push(rule);
        }
        const startKeys = new Set(start.map(refKey));
        const requested = new Set([...startKeys, ...rules.flatMap(r => r.add.map(refKey))]);
        for (let i = 0; i < rules.length; i++) {
            if (!requested.has(refKey(rules[i].when.assay))) {
                return { error: `panel.reflex[${i}].when.assay: ${refKey(rules[i].when.assay)} is neither a start assay nor added by a rule` };
            }
            const restarted = rules[i].add.find(ref => startKeys.has(refKey(ref)));
            if (restarted !== undefined)
                return { error: `panel.reflex[${i}].add: ${refKey(restarted)} is already a start assay` };
        }
        out.reflex = rules;
    }
    return { value: out };
}
/** Every assay the panel names, in first-mention order. */
export function panelAssayRefs(panel) {
    const seen = new Set();
    const out = [];
    const visit = (ref) => {
        if (seen.has(refKey(ref)))
            return;
        seen.add(refKey(ref));
        out.push(ref);
    };
    panel.start.forEach(visit);
    for (const rule of panel.reflex ?? []) {
        visit(rule.when.assay);
        rule.add.forEach(visit);
    }
    return out;
}
export function isResolvedPanel(panel) {
    return panelAssayRefs(panel).every(ref => typeof ref === 'number');
}
/**
 * Replace every title in a validated panel with its assay id. `idForTitle`
 * returns the id, or null when no assay (or more than one) has the title.
 * Errors name every title that did not resolve.
 */
export function resolvePanelAssays(panel, idForTitle) {
    const unresolved = new Set();
    const resolve = (ref) => {
        if (typeof ref === 'number')
            return ref;
        const id = idForTitle(ref);
        if (id === null)
            unresolved.add(ref);
        return id ?? 0;
    };
    const value = { version: panel.version, start: panel.start.map(resolve) };
    if (panel.constants)
        value.constants = panel.constants;
    if (panel.reflex)
        value.reflex = panel.reflex.map(r => ({ ...r, when: { ...r.when, assay: resolve(r.when.assay) }, add: r.add.map(resolve) }));
    if (unresolved.size > 0) {
        return { error: `panel: no single assay titled ${[...unresolved].map(t => `"${t}"`).join(', ')}` };
    }
    const recheck = validateAssayPanel(value);
    if ('error' in recheck)
        return { error: `${recheck.error} (after resolving titles)` };
    return { value };
}
const CLOSED_STATES = new Set(['CANCELLED', 'COMPLETED']);
function compare(value, op, threshold) {
    switch (op) {
        case '>': return value > threshold;
        case '>=': return value >= threshold;
        case '<': return value < threshold;
        case '<=': return value <= threshold;
    }
}
function measuredValue(test, key, analytes) {
    const stored = storedNumber(test.worksheet_data, key);
    if (stored !== null || !analytes)
        return stored;
    const analyte = analyteForKey(analytes, key);
    if (analyte?.kind !== 'derived' || !analyte.formula)
        return null;
    return evaluateDerived(analytes, analyte, k => storedNumber(test.worksheet_data, k));
}
/**
 * What a panel test asks of its sample now. Pure and idempotent: an assay the
 * sample already has a test of, in any state, is never added again, so a
 * cancelled start or follow-up stays cancelled and a replayed plan adds
 * nothing. A condition reads the sample's owning test of its assay and its
 * stored number exactly as the results table does (owningTestsByAssay,
 * storedNumber). A cancelled or completed panel test
 * plans nothing.
 */
export function planPanel(input) {
    const { panel, panelTest, sampleTests } = input;
    if (panelTest.state && CLOSED_STATES.has(panelTest.state)) {
        return { closed: true, testsToAdd: [], panelDone: false, decisions: [], flags: [] };
    }
    const others = sampleTests.filter(t => t.test_id !== panelTest.test_id && t.assay_id != null);
    const hasAnyTest = new Set(others.map(t => t.assay_id));
    const owning = owningTestsByAssay(others);
    const testsToAdd = [];
    const requested = new Set();
    const request = (assayId) => {
        if (requested.has(assayId))
            return;
        requested.add(assayId);
        if (!hasAnyTest.has(assayId))
            testsToAdd.push(assayId);
    };
    panel.start.forEach(request);
    const flags = panel.start
        .filter(id => hasAnyTest.has(id) && !owning.has(id))
        .map(id => ({ code: 'start_cancelled', assay_id: id, message: `every test of start assay ${id} is cancelled` }));
    const rules = panel.reflex ?? [];
    const decisions = new Map();
    let changed = true;
    while (changed) {
        changed = false;
        rules.forEach((rule, index) => {
            if (decisions.get(index)?.outcome === 'fired')
                return;
            const { assay, key, op, value: constantName } = rule.when;
            const constant = panel.constants?.[constantName];
            const decision = {
                rule: index, assay_id: assay, key, op, constant: constantName,
                threshold: constant?.value ?? NaN, unit: constant?.unit ?? null,
                value: null, test_id: null, outcome: 'waiting', add: [...rule.add],
            };
            const test = owning.get(assay);
            if (!requested.has(assay) && !hasAnyTest.has(assay)) {
                decision.outcome = 'unmeasurable';
            }
            else if (!test) {
                decision.outcome = hasAnyTest.has(assay) ? 'unmeasurable' : 'waiting';
            }
            else {
                decision.test_id = test.test_id;
                if (test.state === 'COMPLETED') {
                    decision.value = measuredValue(test, key, input.analytes?.get(assay));
                    if (decision.value !== null && constant) {
                        decision.outcome = compare(decision.value, op, constant.value) ? 'fired' : 'not_fired';
                    }
                }
            }
            if (decision.outcome === 'fired') {
                rule.add.forEach(request);
                changed = true;
            }
            decisions.set(index, decision);
        });
    }
    const ordered = rules.map((_, index) => decisions.get(index));
    for (const d of ordered) {
        if (d.outcome === 'unmeasurable' && hasAnyTest.has(d.assay_id)) {
            flags.push({ code: 'condition_cancelled', assay_id: d.assay_id, rule: d.rule, message: `every test of assay ${d.assay_id} is cancelled; rule ${d.rule} cannot fire` });
        }
        if (d.outcome === 'waiting' && d.test_id !== null && owning.get(d.assay_id)?.state === 'COMPLETED') {
            flags.push({ code: 'missing_value', assay_id: d.assay_id, test_id: d.test_id, rule: d.rule, message: `test ${d.test_id} completed without a number at ${d.key}` });
        }
    }
    return {
        closed: false,
        testsToAdd,
        panelDone: ordered.every(d => d.outcome !== 'waiting'),
        decisions: ordered,
        flags,
    };
}
